import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface BillingEntityConfig {
  id: string;
  tenantId: string;
  practiceEntityId: string;
  practiceEntityCode: string | null;
  invoicePrefix: string;
  invoiceNextNumber: number;
  creditNotePrefix: string;
  debitNotePrefix: string;
  writeOffPrefix: string;
  discountPrefix: string;
  quotePrefix: string;
  defaultTaxRateId: string | null;
  defaultTaxType: string | null;
  defaultPaymentTermsDays: number;
  defaultQuoteExpiryDays: number;
  overdueReminderIntervals: number[];
  paymentReminderDays: number[];
  recurringAdvanceDays: number;
  billingEntityName: string | null;
  billingEntityAddress: string | null;
  billingEntityEmail: string | null;
  billingEntityPhone: string | null;
  billingEntityBusinessNumber: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  headerText: string | null;
  footerText: string | null;
  paymentInstructions: string | null;
  showTaxBreakdown: boolean;
  showDueDate: boolean;
  requireDueDate: boolean;
  roundingPrecision: number;
  currencySymbol: string;
  quantityLabelMode: 'hours' | 'quantity';
  fiscalYearStart: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBillingEntityConfigPayload {
  practice_entity_id: string;
  invoice_prefix?: string;
  invoice_next_number?: number;
  credit_note_prefix?: string;
  debit_note_prefix?: string;
  write_off_prefix?: string;
  discount_prefix?: string;
  quote_prefix?: string;
  default_tax_rate_id?: string | null;
  default_tax_type?: string | null;
  default_payment_terms_days?: number;
  default_quote_expiry_days?: number;
  overdue_reminder_intervals?: number[];
  payment_reminder_days?: number[];
  recurring_advance_days?: number;
  billing_entity_name?: string | null;
  billing_entity_address?: string | null;
  billing_entity_email?: string | null;
  billing_entity_phone?: string | null;
  billing_entity_business_number?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  header_text?: string | null;
  footer_text?: string | null;
  payment_instructions?: string | null;
  show_tax_breakdown?: boolean;
  show_due_date?: boolean;
  require_due_date?: boolean;
  rounding_precision?: number;
  currency_symbol?: string;
  quantity_label_mode?: 'hours' | 'quantity';
  fiscal_year_start?: string | null;
}

export type UpdateBillingEntityConfigPayload = Omit<
  CreateBillingEntityConfigPayload,
  'practice_entity_id'
>;

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const billingEntityConfigKeys = {
  all: ['billing', 'entity-config'] as const,
  list: () => [...billingEntityConfigKeys.all, 'list'] as const,
  detail: (practiceEntityId: string) =>
    [...billingEntityConfigKeys.all, 'detail', practiceEntityId] as const,
};

// ─── Mapper ─────────────────────────────────────────────────────────────────

function mapConfig(raw: Record<string, unknown>): BillingEntityConfig {
  return {
    id: raw.id as string,
    tenantId: (raw.tenant_id || raw.tenantId || '') as string,
    practiceEntityId: (raw.practice_entity_id || raw.practiceEntityId || '') as string,
    practiceEntityCode: (raw.practice_entity_code ?? raw.practiceEntityCode ?? null) as
      | string
      | null,
    invoicePrefix: (raw.invoice_prefix || raw.invoicePrefix || 'INV-') as string,
    invoiceNextNumber: (raw.invoice_next_number ?? raw.invoiceNextNumber ?? 1001) as number,
    creditNotePrefix: (raw.credit_note_prefix || raw.creditNotePrefix || 'CN-') as string,
    debitNotePrefix: (raw.debit_note_prefix || raw.debitNotePrefix || 'DN-') as string,
    writeOffPrefix: (raw.write_off_prefix || raw.writeOffPrefix || 'WO-') as string,
    discountPrefix: (raw.discount_prefix || raw.discountPrefix || 'DSC-') as string,
    quotePrefix: (raw.quote_prefix || raw.quotePrefix || 'QT-') as string,
    defaultTaxRateId: (raw.default_tax_rate_id ?? raw.defaultTaxRateId ?? null) as string | null,
    defaultTaxType: (raw.default_tax_type ?? raw.defaultTaxType ?? null) as string | null,
    defaultPaymentTermsDays: (raw.default_payment_terms_days ??
      raw.defaultPaymentTermsDays ??
      30) as number,
    defaultQuoteExpiryDays: (raw.default_quote_expiry_days ??
      raw.defaultQuoteExpiryDays ??
      30) as number,
    overdueReminderIntervals: (raw.overdue_reminder_intervals ??
      raw.overdueReminderIntervals ?? [1, 7, 14, 30]) as number[],
    paymentReminderDays: (raw.payment_reminder_days ??
      raw.paymentReminderDays ?? [7, 3, 1]) as number[],
    recurringAdvanceDays: (raw.recurring_advance_days ?? raw.recurringAdvanceDays ?? 7) as number,
    billingEntityName: (raw.billing_entity_name ?? raw.billingEntityName ?? null) as string | null,
    billingEntityAddress: (raw.billing_entity_address ?? raw.billingEntityAddress ?? null) as
      | string
      | null,
    billingEntityEmail: (raw.billing_entity_email ?? raw.billingEntityEmail ?? null) as
      | string
      | null,
    billingEntityPhone: (raw.billing_entity_phone ?? raw.billingEntityPhone ?? null) as
      | string
      | null,
    billingEntityBusinessNumber: (raw.billing_entity_business_number ??
      raw.billingEntityBusinessNumber ??
      null) as string | null,
    logoUrl: (raw.logo_url ?? raw.logoUrl ?? null) as string | null,
    primaryColor: (raw.primary_color ?? raw.primaryColor ?? null) as string | null,
    secondaryColor: (raw.secondary_color ?? raw.secondaryColor ?? null) as string | null,
    headerText: (raw.header_text ?? raw.headerText ?? null) as string | null,
    footerText: (raw.footer_text ?? raw.footerText ?? null) as string | null,
    paymentInstructions: (raw.payment_instructions ?? raw.paymentInstructions ?? null) as
      | string
      | null,
    showTaxBreakdown: (raw.show_tax_breakdown ?? raw.showTaxBreakdown ?? true) as boolean,
    showDueDate: (raw.show_due_date ?? raw.showDueDate ?? true) as boolean,
    requireDueDate: (raw.require_due_date ?? raw.requireDueDate ?? false) as boolean,
    roundingPrecision: (raw.rounding_precision ?? raw.roundingPrecision ?? 0) as number,
    currencySymbol: (raw.currency_symbol ?? raw.currencySymbol ?? '$') as string,
    quantityLabelMode: (raw.quantity_label_mode ?? raw.quantityLabelMode ?? 'hours') as
      | 'hours'
      | 'quantity',
    fiscalYearStart: (raw.fiscal_year_start ?? raw.fiscalYearStart ?? null) as string | null,
    isActive: (raw.is_active ?? raw.isActive ?? true) as boolean,
    createdAt: (raw.created_at || raw.createdAt || '') as string,
    updatedAt: (raw.updated_at || raw.updatedAt || '') as string,
  };
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch all billing entity configs for the tenant.
 */
export function useBillingEntityConfigs() {
  return useQuery({
    queryKey: billingEntityConfigKeys.list(),
    queryFn: async () => {
      const response =
        await apiClient.get<ApiResponse<Record<string, unknown>[]>>('/billing/entity-config');
      return (response.data.data || []).map(mapConfig);
    },
  });
}

/**
 * Fetch billing config for a specific practice entity.
 */
export function useBillingEntityConfig(practiceEntityId: string) {
  return useQuery({
    queryKey: billingEntityConfigKeys.detail(practiceEntityId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(
        `/billing/entity-config/${practiceEntityId}`,
      );
      return mapConfig(response.data.data);
    },
    enabled: !!practiceEntityId,
  });
}

/**
 * Create billing config for a practice entity.
 */
export function useCreateBillingEntityConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateBillingEntityConfigPayload) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        '/billing/entity-config',
        data,
      );
      return mapConfig(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingEntityConfigKeys.all });
    },
  });
}

/**
 * Update billing config for a practice entity.
 */
export function useUpdateBillingEntityConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      practiceEntityId,
      data,
    }: {
      practiceEntityId: string;
      data: UpdateBillingEntityConfigPayload;
    }) => {
      const response = await apiClient.patch<ApiResponse<Record<string, unknown>>>(
        `/billing/entity-config/${practiceEntityId}`,
        data,
      );
      return mapConfig(response.data.data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: billingEntityConfigKeys.all });
      queryClient.invalidateQueries({
        queryKey: billingEntityConfigKeys.detail(variables.practiceEntityId),
      });
    },
  });
}
